/**
 * Single source of truth for v3 calendar date-cell appearance.
 *
 * Each date resolves to one visual profile: background layers, text color/weight,
 * and status icon. Tune the look by editing `CALENDAR_DATE_CELL_TUNING` below.
 *
 * Resolution order (documented for each concern):
 *
 * Background layers (applied in order - later entries override same properties):
 *   1. frozen -> 2. day off -> 3. meal state (booked / partial / select) -> 4. past opacity -> 5. today tint
 *
 * Text color (first match wins):
 *   booked -> partial -> frozen -> day off -> past -> today -> default
 *
 * Icon (first match wins):
 *   today + active plan -> frozen -> API icon -> day off -> meal state -> default (null)
 */
import type { TextStyle, ViewStyle } from 'react-native';
import { hexToRgba } from '../../shared/theme/colorUtils';
import type { ThemeColors } from '../../shared/theme/store';
import { getIconFromApiFormat, type CalendarDay } from '../../data/domains/calendar';

// Fine-tuning knobs. Edit these values to adjust the calendar look without hunting through components.

export const CALENDAR_DATE_CELL_TUNING = {
  booked: {
    backgroundOpacity: 0.22,
    borderOpacity: 0.25,
    borderWidth: 1,
    textWeight: '600' as const,
  },
  partial: {
    backgroundOpacity: 0.12,
    textWeight: '600' as const,
  },
  selectMeal: {
    backgroundOpacity: 0.06,
  },
  today: {
    borderOpacity: 0.25,
    borderWidth: 1,
    backgroundOpacity: 0.1,
    textWeight: '600' as const,
  },
  frozen: {
    /** Uses theme `alertFade` when set; falls back to alert at this opacity. */
    borderOpacity: 0.25,
    borderWidth: 1,
    backgroundOpacity: 0.05,
  },
  /** Backend pause — alert/info tint (not warning: that is partial booking). */
  paused: {
    borderOpacity: 0.3,
    borderWidth: 1,
    backgroundOpacity: 0.1,
  },
  /** Double-box delivery day — primary tint (distinct from partial warning). */
  doubleBox: {
    borderOpacity: 0.28,
    borderWidth: 1,
    backgroundOpacity: 0.1,
  },
  dayOff: {
    /** Background alpha for day-off wash (0-1). Prefer over hex suffix for Android/iOS parity. */
    backgroundOpacity: 0.19,
  },
  past: {
    cellOpacity: 0.75,
  },
  defaultText: {
    fontWeight: '500' as const,
  },
  icons: {
    todayActivePlan: 'driver' as const,
    frozen: 'freeze' as const,
    /** Lock = backend-hold (not user freeze bars). */
    paused: 'lock' as const,
    doubleBox: 'gift' as const,
    dayOff: 'x' as const,
    booked: 'check' as const,
    partial: 'package' as const,
    selectMeal: 'plus' as const,
    feedback: 'star' as const,
    skipped: 'x' as const,
    onTheWay: 'driver' as const,
  },
} as const;

export type CalendarDateCellIcon = { name: string; color: string; size?: number };

/** Looping motion presets - tune in `CALENDAR_DATE_CELL_ANIMATIONS`. */
export type CalendarDateCellAnimationPreset =
  | 'todayFloat'
  | 'selectMealPulse'
  | 'bookedSettle'
  | 'partialAttention'
  | 'selectMealInvite'
  | 'frozenIdle'
  | 'onTheWayDrift'
  | 'feedbackShimmer';

export interface CalendarDateCellAnimationConfig {
  /** Full loop duration (ms). Phase runs 0 -> 2pi linearly, then wraps seamlessly. */
  cycle_ms: number;
  scale_from: number;
  scale_to: number;
  translate_y_from: number;
  translate_y_to: number;
}

export const CALENDAR_DATE_CELL_ANIMATION_TWO_PI = 2 * Math.PI;

export const CALENDAR_DATE_CELL_ANIMATIONS = {
  /** Gentle bob + breathe on today's cell. */
  todayFloat: {
    cycle_ms: 1800,
    scale_from: 1,
    scale_to: 1.06,
    translate_y_from: 0,
    translate_y_to: -2,
  },
  /** Scale-only pulse for available select-meal days. */
  selectMealPulse: {
    cycle_ms: 2200,
    scale_from: 1,
    scale_to: 1.04,
    translate_y_from: 0,
    translate_y_to: 0,
  },
  /** Soft settle pulse on fully booked days. */
  bookedSettle: {
    cycle_ms: 2400,
    scale_from: 1,
    scale_to: 1.03,
    translate_y_from: 0,
    translate_y_to: -1,
  },
  /** Light nudge on partially booked days. */
  partialAttention: {
    cycle_ms: 2000,
    scale_from: 1,
    scale_to: 1.035,
    translate_y_from: 0,
    translate_y_to: -1,
  },
  /** Inviting pulse on open select-meal slots. */
  selectMealInvite: {
    cycle_ms: 1900,
    scale_from: 1,
    scale_to: 1.05,
    translate_y_from: 0,
    translate_y_to: 0,
  },
  /** Minimal idle pulse on frozen/paused days. */
  frozenIdle: {
    cycle_ms: 1600,
    scale_from: 1,
    scale_to: 1.02,
    translate_y_from: 0,
    translate_y_to: 0,
  },
  /** Vertical drift for on-the-way delivery days. */
  onTheWayDrift: {
    cycle_ms: 1400,
    scale_from: 1,
    scale_to: 1,
    translate_y_from: 0,
    translate_y_to: -1.5,
  },
  /** Subtle shimmer for feedback/rating days. */
  feedbackShimmer: {
    cycle_ms: 2100,
    scale_from: 1,
    scale_to: 1.04,
    translate_y_from: 0,
    translate_y_to: -0.5,
  },
} as const satisfies Record<CalendarDateCellAnimationPreset, CalendarDateCellAnimationConfig>;

/** UI-thread transform from phase angle - `(1 - cos(phase)) / 2` loops with zero velocity at wrap. */
export function calendarDateCellAnimationTransform(
  phase: number,
  scale_from: number,
  scale_to: number,
  translate_y_from: number,
  translate_y_to: number,
): { scale: number; translateY: number } {
  'worklet';
  const t = (1 - Math.cos(phase)) * 0.5;
  return {
    scale: scale_from + (scale_to - scale_from) * t,
    translateY: translate_y_from + (translate_y_to - translate_y_from) * t,
  };
}

export interface CalendarDateCellVisual {
  /** Background + modifier styles applied to the outer cell in order. */
  cellStyles: ViewStyle[];
  /** Text style merged onto the day number (null = use grid default). */
  textStyle: TextStyle | null;
  icon: CalendarDateCellIcon | null;
  /** Optional looping motion preset (rendered in `CalendarDateCellAnimation`). */
  animation: CalendarDateCellAnimationPreset | null;
}

export interface ResolveCalendarDateCellVisualInput {
  day: CalendarDay;
  theme: ThemeColors;
  /** Whether this date is today in the currently displayed month. */
  is_today_in_view: boolean;
  is_past: boolean;
  has_active_plan: boolean;
}

export interface CalendarLegendColors {
  mealSelected: string;
  /** User freeze (snowflake) — error tint. */
  frozenDay: string;
  /** Backend pause — warning tint. */
  pausedDay: string;
  offDay: string;
  mealSelectedBackground: string;
  frozenDayBackground: string;
  pausedDayBackground: string;
  offDayBackground: string;
}

/** Legend swatches - kept in sync with cell visuals. */
export function getCalendarLegendColors(theme: ThemeColors): CalendarLegendColors {
  const tuning = CALENDAR_DATE_CELL_TUNING;
  return {
    mealSelectedBackground: theme.successUltra,
    mealSelected: hexToRgba(theme.success, tuning.booked.backgroundOpacity) ?? theme.successFade,
    frozenDayBackground: theme.errorUltra,
    frozenDay: hexToRgba(theme.error, tuning.frozen.borderOpacity) ?? theme.errorFade,
    pausedDayBackground: theme.alertUltra ?? theme.alertFade,
    pausedDay: hexToRgba(theme.alert, tuning.paused.borderOpacity) ?? theme.alertFade ?? theme.alert,
    offDayBackground: theme.textLight,
    offDay: hexToRgba(theme.textLight, tuning.dayOff.backgroundOpacity) ?? theme.textSecondary,
  };
}

export interface CalendarFreezeActionColors {
  border: string;
  background: string;
  foreground: string;
  disabled_background: string;
}

/** Freeze CTA / pill colors - matches `CalendarScreen` freeze action button. */
export function getFreezeActionColors(theme: ThemeColors): CalendarFreezeActionColors {
  return {
    border: (hexToRgba(theme.error, 0.2) as string) || theme.error,
    background: (hexToRgba(theme.error, 0.12) as string) || theme.errorFade,
    foreground: theme.error,
    disabled_background: (hexToRgba(theme.error, 0.28) as string) || theme.errorFade,
  };
}

/** Unfreeze CTA / pill colors - matches `CalendarScreen` unfreeze action button. */
export function getUnfreezeActionColors(theme: ThemeColors): CalendarFreezeActionColors {
  return {
    border: (hexToRgba(theme.success, 0.2) as string) || theme.success,
    background: (hexToRgba(theme.success, 0.14) as string) || theme.successUltra,
    foreground: theme.success,
    disabled_background: (hexToRgba(theme.success, 0.28) as string) || theme.successFade,
  };
}

/** Frozen-day highlight in freeze/unfreeze sheets - matches main calendar grid paused cells. */
export function getFrozenDayHighlightColors(theme: ThemeColors) {
  const tuning = CALENDAR_DATE_CELL_TUNING;
  const border = hexToRgba(theme.error, tuning.frozen.borderOpacity) ?? theme.error;
  const background = theme.errorFade ?? hexToRgba(theme.error, tuning.frozen.backgroundOpacity);
  return {
    background,
    border,
    text: theme.error,
    legend_swatch: background,
    legend_border: border,
  };
}

type MealStateStyle = 'booked' | 'partial' | 'select';

function mealStateStyle(day: CalendarDay): MealStateStyle | null {
  if (day.state === 'Booked Meal') {
    return 'booked';
  }
  if (day.state === 'Partially Booked Meal') {
    return 'partial';
  }
  if (day.state === 'Select Meal') {
    return 'select';
  }
  return null;
}

function backgroundForMealState(state: MealStateStyle, theme: ThemeColors): ViewStyle {
  const tuning = CALENDAR_DATE_CELL_TUNING;
  if (state === 'booked') {
    return {
      borderWidth: tuning.booked.borderWidth,
      borderStyle: 'solid',
      borderColor: hexToRgba(theme.success, tuning.booked.borderOpacity) ?? theme.successUltra,
      backgroundColor: hexToRgba(theme.success, tuning.booked.backgroundOpacity) ?? theme.successUltra,
    };
  }
  if (state === 'partial') {
    return {
      borderWidth: tuning.booked.borderWidth,
      borderStyle: 'solid',
      borderColor: hexToRgba(theme.warning, tuning.booked.borderOpacity) ?? theme.warningUltra,
      backgroundColor: hexToRgba(theme.warning, tuning.partial.backgroundOpacity) ?? theme.warningUltra,
    };
  }
  return {
    backgroundColor: hexToRgba(theme.primary, tuning.selectMeal.backgroundOpacity) ?? theme.primaryFade,
  };
}

function textStyleForMealState(state: MealStateStyle, theme: ThemeColors): TextStyle {
  const weight = state === 'select' ? CALENDAR_DATE_CELL_TUNING.defaultText.fontWeight : CALENDAR_DATE_CELL_TUNING.booked.textWeight;
  const color = state === 'booked' ? theme.success : theme.text;

  return { color: color, fontWeight: weight };
}

function tintForMappedApiIcon(icon_raw: string, mapped: string, theme: ThemeColors): CalendarDateCellIcon {
  const low = icon_raw.toLowerCase();
  if (mapped === 'check') {
    if (low.includes('fa-duotone')) {
      return { name: 'package', color: theme.warning };
    } else {
      return { name: 'check', color: theme.success, size: 18 };
    }
  }
  if (mapped === 'x' || low.includes('fa-ban') || low.includes('fa-times')) {
    return { name: mapped, color: theme.textLight };
  }
  if (mapped === 'driver' || low.includes('truck')) {
    return { name: mapped, color: theme.surface };
  }
  if (mapped === 'star' || low.includes('star')) {
    return { name: mapped, color: theme.warningLight };
  }
  if (mapped === 'plus' || low.includes('utensils')) {
    return { name: mapped, color: theme.primary };
  }
  if (mapped === 'package' || low.includes('fa-plate')) {
    return { name: mapped, color: theme.warning };
  }
  if (low.includes('pause')) {
    return { name: mapped, color: theme.error, size: 18 };
  }
  if (low.includes('snow') || low.includes('snowflake')) {
    return { name: mapped, color: theme.error, size: 18 };
  }
  return { name: mapped, color: theme.primary };
}

function resolveIcon(day: CalendarDay, theme: ThemeColors, has_active_plan: boolean, is_today_in_view: boolean): CalendarDateCellIcon | null {
  const icons = CALENDAR_DATE_CELL_TUNING.icons;

  if (is_today_in_view && has_active_plan && !day.isFrozen && !day.isPaused && !day.isDayOff) {
    return { name: icons.todayActivePlan, color: theme.alert, size: 18 };
  }
  if (day.isFrozen) {
    return { name: icons.frozen, color: theme.error, size: 16 };
  }
  if (day.isPaused) {
    return { name: icons.paused, color: theme.alert, size: 16 };
  }
  if (day.icon) {
    const mapped = getIconFromApiFormat(day.icon);
    if (mapped) {
      return tintForMappedApiIcon(day.icon, mapped, theme);
    }
  }
  if (day.isDayOff) {
    return { name: icons.dayOff, color: theme.textLight };
  }
  if (day.state === 'Booked Meal') {
    return { name: icons.booked, color: theme.success };
  }
  if (day.state === 'Partially Booked Meal') {
    return { name: icons.partial, color: theme.warning };
  }
  if (day.isDoubleBox) {
    return { name: icons.doubleBox, color: theme.primary, size: 14 };
  }
  if (day.state === 'Select Meal') {
    return { name: icons.selectMeal, color: theme.primary };
  }
  if (day.state === 'Feedback') {
    return { name: icons.feedback, color: theme.warningLight };
  }
  if (day.state === 'Skipped') {
    return { name: icons.skipped, color: theme.textLight };
  }
  if (day.state === 'On the way') {
    return { name: icons.onTheWay, color: theme.primary };
  }
  return null;
}

function resolveTextStyle(
  day: CalendarDay,
  theme: ThemeColors,
  meal_state: MealStateStyle | null,
  is_today_in_view: boolean,
  is_past: boolean,
): TextStyle | null {
  if (meal_state === 'booked' || meal_state === 'partial') {
    return textStyleForMealState(meal_state, theme);
  }
  if (day.isFrozen) {
    return { color: theme.error ?? theme.error };
  }
  if (day.isPaused) {
    return { color: theme.alert };
  }
  if (day.isDoubleBox) {
    return { color: theme.primary, fontWeight: CALENDAR_DATE_CELL_TUNING.defaultText.fontWeight };
  }
  if (day.isDayOff) {
    return { color: theme.textLight };
  }
  if (is_past) {
    return { color: theme.textLight };
  }
  if (is_today_in_view) {
    return { color: theme.alert, fontWeight: CALENDAR_DATE_CELL_TUNING.today.textWeight };
  }
  return null;
}

function resolveCellStyles(
  day: CalendarDay,
  theme: ThemeColors,
  meal_state: MealStateStyle | null,
  is_today_in_view: boolean,
  is_past: boolean,
): ViewStyle[] {
  const styles: ViewStyle[] = [];
  const tuning = CALENDAR_DATE_CELL_TUNING;

  if (day.isFrozen) {
    styles.push({
      borderWidth: tuning.frozen.borderWidth,
      borderStyle: 'solid',
      borderColor: hexToRgba(theme.error, tuning.frozen.borderOpacity) ?? theme.error,
      backgroundColor: theme.errorFade ?? hexToRgba(theme.error, tuning.frozen.backgroundOpacity),
    });
  }
  if (day.isPaused) {
    styles.push({
      borderWidth: tuning.paused.borderWidth,
      borderStyle: 'solid',
      borderColor: hexToRgba(theme.alert, tuning.paused.borderOpacity) ?? theme.alert,
      backgroundColor: theme.alertFade ?? hexToRgba(theme.alert, tuning.paused.backgroundOpacity),
    });
  }
  if (day.isDoubleBox) {
    styles.push({
      borderWidth: tuning.doubleBox.borderWidth,
      borderStyle: 'solid',
      borderColor: hexToRgba(theme.primary, tuning.doubleBox.borderOpacity) ?? theme.primary,
      backgroundColor: theme.primaryUltra ?? hexToRgba(theme.primary, tuning.doubleBox.backgroundOpacity),
    });
  }
  if (day.isDayOff) {
    styles.push({
      backgroundColor: hexToRgba(theme.textLight, tuning.dayOff.backgroundOpacity) ?? theme.textLight,
    });
  }
  if (meal_state) {
    styles.push(backgroundForMealState(meal_state, theme));
  }
  if (is_past) {
    styles.push({ opacity: tuning.past.cellOpacity });
  }
  if (is_today_in_view) {
    styles.push({
      borderWidth: tuning.today.borderWidth,
      borderStyle: 'solid',
      borderColor: hexToRgba(theme.alert, tuning.today.borderOpacity) ?? theme.alert,
      backgroundColor: hexToRgba(theme.alert, tuning.today.backgroundOpacity) ?? theme.alertFade,
    });
  }
  return styles;
}

function resolveAnimation(
  day: CalendarDay,
  meal_state: MealStateStyle | null,
  is_today_in_view: boolean,
): CalendarDateCellAnimationPreset | null {
  const is_blocked = day.isFrozen || day.isPaused || day.isDayOff;

  // if (is_today_in_view && !is_blocked) {
  //   return 'todayFloat';
  // }

  // Uncomment one line at a time while tuning presets:
  // if (!is_blocked && meal_state === 'booked') return 'bookedSettle';
  // if (!is_blocked && meal_state === 'partial') return 'partialAttention';
  // if (!is_blocked && meal_state === 'select') return 'selectMealInvite';
  if (day.isFrozen) return 'frozenIdle';
  if (day.isPaused) return 'frozenIdle';
  if (!is_blocked && day.state === 'On the way') return 'onTheWayDrift';
  if (day.state === 'Feedback') return 'feedbackShimmer';
  // if (!is_blocked && meal_state === 'select') return 'selectMealPulse';

  return null;
}

/** Resolve the full visual profile for one calendar date cell. */
export function resolveCalendarDateCellVisual(input: ResolveCalendarDateCellVisualInput): CalendarDateCellVisual {
  const { day, theme, is_today_in_view, is_past, has_active_plan } = input;
  const meal_state = mealStateStyle(day);

  return {
    cellStyles: resolveCellStyles(day, theme, meal_state, is_today_in_view, is_past),
    textStyle: resolveTextStyle(day, theme, meal_state, is_today_in_view, is_past),
    icon: resolveIcon(day, theme, has_active_plan, is_today_in_view),
    animation: resolveAnimation(day, meal_state, is_today_in_view),
  };
}
